package com.anjuros.website.redesign.sections.seo

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdBreadcrumb
import com.anjuros.website.redesign.components.RdGhostButton
import com.anjuros.website.redesign.components.RdLede
import com.anjuros.website.redesign.components.RdPrimaryButton
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.styles.RdButtonRowStyle
import com.anjuros.website.redesign.styles.RdHeroCopyStyle
import com.anjuros.website.redesign.styles.RdHeroStyle
import com.anjuros.website.redesign.styles.RdMarkStyle
import com.anjuros.website.redesign.styles.RdSubpageTitleStyle
import com.anjuros.website.redesign.styles.SeoGhostResultStyle
import com.anjuros.website.redesign.styles.SeoHeroGridStyle
import com.anjuros.website.redesign.styles.SeoNotesStyle
import com.anjuros.website.redesign.styles.SeoResultSiteStyle
import com.anjuros.website.redesign.styles.SeoResultStyle
import com.anjuros.website.redesign.styles.SeoResultTagStyle
import com.anjuros.website.redesign.styles.SeoResultTextStyle
import com.anjuros.website.redesign.styles.SeoResultTitleStyle
import com.anjuros.website.redesign.styles.SeoSerpBarStyle
import com.anjuros.website.redesign.styles.SeoSerpMetaStyle
import com.anjuros.website.redesign.styles.SeoSerpStyle
import com.anjuros.website.redesign.util.RdIcons
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.redesign.util.rdList
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.I
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/** One example search: what was typed, and the title and description SEO wrote for the result. */
private class SearchExample(val query: String, val title: String, val description: String)

/** Each text is "query~title~description". */
private val SearchExampleKeys = listOf(
    RdText.SEO_SERP_EXAMPLE1,
    RdText.SEO_SERP_EXAMPLE2,
    RdText.SEO_SERP_EXAMPLE3,
    RdText.SEO_SERP_EXAMPLE4,
)

@Composable
fun SeoHeroSection() {
    RdSection(labelledBy = "h1", modifier = RdHeroStyle.toModifier(), wrapModifier = SeoHeroGridStyle.toModifier()) {
        Div(RdHeroCopyStyle.toModifier().toAttrs()) {
            RdBreadcrumb(RdText.SEO_CRUMB)
            H1(RdSubpageTitleStyle.toModifier().id("h1").toAttrs()) {
                Text(LocalText(RdText.SEO_HERO_TITLE_START))
                Span(RdMarkStyle.toModifier().toAttrs()) { Text(LocalText(RdText.SEO_HERO_TITLE_MARK)) }
            }
            RdLede(RdText.SEO_HERO_LEDE)
            Div(RdButtonRowStyle.toModifier().toAttrs()) {
                RdPrimaryButton("#included", RdText.SEO_HERO_INCLUDED, withArrow = true)
                RdGhostButton(RdRoute.CONTACT, RdText.CTA_FREE_PREVIEW)
            }
        }
        SearchResultMock()
    }
}

/** A search result for "your website", cycling through example trades and towns. */
@Composable
private fun SearchResultMock() {
    val examples = SearchExampleKeys.map { key ->
        val (query, title, description) = LocalText(key).split("~")
        SearchExample(query, title, description)
    }
    var index by remember { mutableStateOf(0) }

    LaunchedEffect(examples.size) {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return@LaunchedEffect
        while (true) {
            delay(3800)
            index = (index + 1) % examples.size
        }
    }

    val example = examples[index]
    Div(SeoSerpStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) {
        Div(SeoSerpBarStyle.toModifier().toAttrs()) {
            RdIcons.Search(size = 18)
            Span { Text(example.query) }
        }
        P(SeoSerpMetaStyle.toModifier().toAttrs()) { Text(LocalText(RdText.SEO_SERP_EXAMPLE)) }
        Div(SeoResultStyle.toModifier().toAttrs()) {
            Span(SeoResultTagStyle.toModifier().toAttrs()) { Text(LocalText(RdText.SEO_SERP_TAG)) }
            Div(SeoResultSiteStyle.toModifier().toAttrs()) {
                I { Text("YB") }
                Span { Text(LocalText(RdText.SEO_SERP_SITE)) }
            }
            P(SeoResultTitleStyle.toModifier().toAttrs()) { Text(example.title) }
            P(SeoResultTextStyle.toModifier().toAttrs()) { Text(example.description) }
        }
        repeat(2) { Div(SeoGhostResultStyle.toModifier().toAttrs()) { repeat(3) { Span() } } }
        Ul(SeoNotesStyle.toModifier().toAttrs()) {
            rdList(LocalText(RdText.SEO_SERP_NOTES)).forEach { Li { Text(it) } }
        }
    }
}
